/**
 * Westdale Peptides — Hero Carousel
 * Role-based 4-item carousel with 650ms crossfade transitions.
 */
class WPHeroCarousel {
  constructor(section) {
    this.section   = section;
    this.active    = 0;
    this.animating = false;

    this.items   = Array.from(section.querySelectorAll('.wp-hero__item'));
    this.dots    = Array.from(section.querySelectorAll('.wp-hero__dot'));
    this.prevBtn = section.querySelector('[data-hero-prev]');
    this.nextBtn = section.querySelector('[data-hero-next]');

    if (this.items.length < 2) return;

    // Mark header transparent when this hero is on the page
    document.querySelector('.section-header')?.classList.add('wp-hero-page');

    this._preload();
    this._setRoles();
    this._updateDots();
    this._bind();
  }

  // ── Role helpers (works for 2–6 slides) ──────────────
  _setRoles() {
    const n = this.items.length;
    this.items.forEach((el, j) => {
      const offset = (j - this.active + n) % n;
      let role;
      if      (offset === 0)          role = 'center';
      else if (offset === 1)          role = 'right';
      else if (n > 2 && offset === n - 1) role = 'left';
      else if (n >= 4 && offset === 2)    role = 'back';
      else                            role = 'extra';
      el.dataset.role = role;
    });
  }

  // ── Navigation ────────────────────────────────────────
  navigate(dir) {
    if (this.animating) return;
    this.animating = true;

    const n = this.items.length;
    this.active = dir === 'next'
      ? (this.active + 1) % n
      : (this.active + n - 1) % n;

    this._setRoles();
    this._updateDots();

    setTimeout(() => { this.animating = false; }, 650);
  }

  // ── Background color ──────────────────────────────────
  _updateBg() {
    const bg = this.items[this.active]?.dataset.bg;
    if (bg) this.section.style.backgroundColor = bg;
  }

  // ── Dot indicators ────────────────────────────────────
  _updateDots() {
    this.dots.forEach((dot, i) => {
      dot.dataset.active = (i === this.active).toString();
    });
  }

  // ── Image preload ─────────────────────────────────────
  _preload() {
    this.items.forEach(item => {
      const src = item.querySelector('img')?.src;
      if (src) { const img = new Image(); img.src = src; }
    });
  }

  // ── Event bindings ────────────────────────────────────
  _bind() {
    this.prevBtn?.addEventListener('click', () => this.navigate('prev'));
    this.nextBtn?.addEventListener('click', () => this.navigate('next'));

    // Dot clicks
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        if (this.animating || i === this.active) return;
        this.animating = true;
        this.active = i;
        this._setRoles();
        this._updateDots();
        setTimeout(() => { this.animating = false; }, 650);
      });
    });

    // Keyboard
    this.section.setAttribute('tabindex', '0');
    this.section.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  this.navigate('prev');
      if (e.key === 'ArrowRight') this.navigate('next');
    });

    // Touch swipe
    let x0 = 0;
    this.section.addEventListener('touchstart', e => {
      x0 = e.touches[0].clientX;
    }, { passive: true });
    this.section.addEventListener('touchend', e => {
      const dx = x0 - e.changedTouches[0].clientX;
      if (Math.abs(dx) > 50) this.navigate(dx > 0 ? 'next' : 'prev');
    }, { passive: true });
  }
}

// ── Init ─────────────────────────────────────────────────
function initWPHero() {
  document.querySelectorAll('.wp-hero-section').forEach(el => {
    if (!el._wpHero) el._wpHero = new WPHeroCarousel(el);
  });
}

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initWPHero)
  : initWPHero();

// Shopify theme editor re-init on section reload
document.addEventListener('shopify:section:load', e => {
  const section = e.target.querySelector('.wp-hero-section');
  if (section) { section._wpHero = null; initWPHero(); }
});
